import type { Portfolio } from "@/types/Portfolio";

/**
 * The order of a widget's highlighted lists: when an item matches more than
 * one, the first in this order paints it.
 *
 * Its own module, importing only types: these are the rules worth testing, and
 * the hooks that use them reach `window` on import.
 *
 * The order belongs to the widget instance, not to the lists. A list used to
 * carry a global priority, but which of two lists should win only means
 * something where both are switched on together — and two Headlines widgets
 * can want the same pair the other way round. It is saved with the widget's
 * selections (`WidgetFilterData.highlightOrder`), so a popped-out widget, which
 * is the same instance, keeps it too.
 *
 * Keys name the store as well as the id: watchlist 7 and combined watchlist 7
 * are different lists, which bare ids could not tell apart.
 */
export type HighlightKey = string;

export const WATCHLIST_HIGHLIGHT_SECTIONS = [
  "ticker-watchlists-highlight",
  "keyword-watchlists-highlight",
] as const;
export const COMBINED_HIGHLIGHT_SECTION = "category-highlight";

type Selections = Record<string, string[]>;

/** The key for an id ticked under a highlight section; none for other sections. */
export function listKey(sectionId: string, id: string | number): HighlightKey | undefined {
  if ((WATCHLIST_HIGHLIGHT_SECTIONS as readonly string[]).includes(sectionId)) {
    return `watchlist:${id}`;
  }
  if (sectionId === COMBINED_HIGHLIGHT_SECTION) return `combined:${id}`;
  return undefined;
}

export function parseKey(
  key: HighlightKey
): { kind: "watchlist" | "combined"; id: number } | undefined {
  const [kind, raw] = key.split(":");
  const id = Number(raw);
  if ((kind !== "watchlist" && kind !== "combined") || !raw || !Number.isFinite(id)) {
    return undefined;
  }
  return { kind, id };
}

/**
 * Every highlighted list, in the default order: ticker lists, then keyword
 * lists, then combined lists, each in the order they were ticked. It decides
 * only for lists ticked with no saved order around them — existing widgets
 * were given one from the old priorities (seedOrder).
 */
export function highlightedKeys(selections: Selections | undefined): HighlightKey[] {
  const keys: HighlightKey[] = [];
  for (const section of [...WATCHLIST_HIGHLIGHT_SECTIONS, COMBINED_HIGHLIGHT_SECTION]) {
    for (const id of selections?.[section] ?? []) {
      const key = listKey(section, id);
      if (key && !keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

/**
 * The order to resolve by: the saved order's lists that are still ticked, then
 * any ticked list it doesn't mention, in the default order.
 *
 * Unticked and deleted lists drop out here rather than being cleaned up at
 * every place that removes one — templates, the concierge and conversions all
 * write selections without an order.
 */
export function effectiveOrder(
  selections: Selections | undefined,
  stored: HighlightKey[] | undefined
): HighlightKey[] {
  const ticked = highlightedKeys(selections);
  if (!stored?.length) return ticked;
  const tickedSet = new Set(ticked);
  const kept = stored.filter((key, i) => tickedSet.has(key) && stored.indexOf(key) === i);
  const keptSet = new Set(kept);
  return [...kept, ...ticked.filter((key) => !keptSet.has(key))];
}

/**
 * The order after a change of selections: what was showing keeps its place, a
 * newly ticked list joins the bottom, and an unticked one leaves.
 */
export function reconcileOrder(
  stored: HighlightKey[] | undefined,
  before: Selections | undefined,
  after: Selections | undefined
): HighlightKey[] {
  const previous = effectiveOrder(before, stored);
  const now = highlightedKeys(after);
  const nowSet = new Set(now);
  const kept = previous.filter((key) => nowSet.has(key));
  const keptSet = new Set(kept);
  return [...kept, ...now.filter((key) => !keptSet.has(key))];
}

/** One place up (-1) or down (+1); unchanged at either end. */
export function moveKey(
  order: HighlightKey[],
  key: HighlightKey,
  delta: -1 | 1
): HighlightKey[] {
  const from = order.indexOf(key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = [...order];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/**
 * A list that became another — a watchlist converted to a combined watchlist —
 * keeps its place. If the new list was already in the order, the old one just
 * leaves.
 */
export function replaceKey(
  order: HighlightKey[] | undefined,
  from: HighlightKey,
  to: HighlightKey
): HighlightKey[] | undefined {
  if (!order?.includes(from)) return order;
  if (order.includes(to)) return order.filter((key) => key !== from);
  return order.map((key) => (key === from ? to : key));
}

/**
 * The order that reproduces what a widget showed while lists had a priority.
 *
 * Used once per widget, when it is first loaded after priority moved to the
 * widget, so nobody's highlights change colour on the day it ships:
 *
 *   - watchlists by their priority, lower first — with the user's own
 *     priority for a Newsquawk list, which was kept in the browser;
 *   - ties in list-id order, which is how Headlines broke them (the lists
 *     came in API order, by id, and the sort was stable);
 *   - then combined watchlists, which yielded to any watchlist, in the order
 *     they were ticked.
 *
 * The equities feeds were the exception: there a combined list took priority
 * 1, beating most watchlists. They get the rule every other widget used.
 */
export function seedOrder(
  selections: Selections | undefined,
  portfolios: Pick<Portfolio, "id" | "owner_type" | "priority">[],
  newsquawkPriorities: Record<number, number | undefined>
): HighlightKey[] {
  const byId = new Map(portfolios.map((p) => [p.id, p]));
  const priorityOf = (p: Pick<Portfolio, "id" | "owner_type" | "priority">) =>
    (p.owner_type === "NEWSQUAWK" ? newsquawkPriorities[p.id] : undefined) ??
    p.priority ??
    0;

  const watchlists = highlightedKeys(selections)
    .map(parseKey)
    .filter((parsed): parsed is { kind: "watchlist"; id: number } => parsed?.kind === "watchlist")
    .map(({ id }) => byId.get(id))
    .filter((p): p is Pick<Portfolio, "id" | "owner_type" | "priority"> => !!p)
    .sort((a, b) => priorityOf(a) - priorityOf(b) || a.id - b.id)
    .map((p) => `watchlist:${p.id}`);

  const combined = (selections?.[COMBINED_HIGHLIGHT_SECTION] ?? []).map(
    (id) => `combined:${id}`
  );

  return [...watchlists, ...combined.filter((key, i) => combined.indexOf(key) === i)];
}
